import React, { useEffect, useMemo, useState } from 'react';
import { ApiScreeningMatchProvider } from '../../../api';

import useCustomLists from '../../../custom-lists/useCustomLists';
import { createColumnHelper } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPencil } from '@fortawesome/pro-solid-svg-icons';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import './DowJonesSignificantFieldsModal.scss';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import { SalvButton, SalvCheckbox, SalvModal, SalvTable, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import { InternalApiCustomListSignificantFieldConf, InternalApiEntityType } from '../../../api-internal';
import { customListSignificantFieldApi } from '../../../apis';
import { fetchSignificantFieldsSuccess } from '../../../custom-lists/customListsReducer';
import { DowJonesCustomListCode, dowJonesListDisplayNamesOLD, dowJonesSortOrder } from '../ScreeningDataSetup';
import { useMe } from '../../../organisation/useMe';

interface SignificantFieldByField {
  fieldName: string;
  fieldLists: { listId: string; listName: string; fieldId: string; isSignificant: boolean }[];
}

interface Props {
  buttonSize?: Size;
  entityType: InternalApiEntityType;
  significantFieldsByField: SignificantFieldByField[];
}

const DowJonesSignificantFieldsModal = ({ buttonSize = Size.SMALL, entityType, significantFieldsByField }: Props) => {
  const customLists = useCustomLists();
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { features } = useMe();
  const isAdverseMediaEntitiesEnabled = isFeatureEnabled(ORGANISATION_FEATURE.ADVERSE_MEDIA_ENTITIES, features);

  const handleClose = () => {
    setShowModal(false);
  };

  const [significantFieldConf, setSignificantFieldConf] = useState<InternalApiCustomListSignificantFieldConf[]>([]);

  useEffect(() => {
    const newSignificantFieldConf: InternalApiCustomListSignificantFieldConf[] = [];
    significantFieldsByField.forEach((significantFieldByField) => {
      significantFieldByField.fieldLists.forEach((fieldList) => {
        if (!fieldList.isSignificant) {
          return;
        }

        const listConfig = newSignificantFieldConf.find((listConfig) => listConfig.listId === fieldList.listId);

        if (listConfig) {
          listConfig.fields.push(fieldList.fieldId);
        } else {
          newSignificantFieldConf.push({
            listId: fieldList.listId,
            fields: [fieldList.fieldId],
          });
        }
      });
    });
    setSignificantFieldConf(newSignificantFieldConf);
  }, [significantFieldsByField]);

  const submit = async () => {
    setShowModal(false);
    dispatch(
      showConfirmationModal({
        title: 'Save new Significant Update Indicators from List records?',
        description:
          'Modifying these settings could significantly increase the number of new alerts or ' +
          'prevent certain alerts from reoccurring. Are you certain you wish to proceed with saving ' +
          'these changes?',
        confirmationText: 'Yes',
        cancelText: 'No, go back',
        onConfirm() {
          updateConfiguration();
        },
        onCancel() {
          setShowModal(true);
        },
      })
    );
  };

  const updateConfiguration = async () => {
    setSubmitting(true);
    const res = await customListSignificantFieldApi.updateSignificantFields({
      internalApiCustomListSignificantFields: { entityType, _configuration: significantFieldConf },
    });
    dispatch(fetchSignificantFieldsSuccess({ customListSignificantFields: res }));

    setSubmitting(false);
  };

  const columnHelper = createColumnHelper<SignificantFieldByField>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('fieldName', { header: 'Field' }),
      ...(customLists
        .filter((customList) => customList.code?.startsWith(ApiScreeningMatchProvider.DOW_JONES))
        .filter(
          (customList) => customList.code !== DowJonesCustomListCode.DOW_JONES_AME || isAdverseMediaEntitiesEnabled
        )
        .toSorted(
          (a, b) =>
            dowJonesSortOrder.indexOf(a.code as DowJonesCustomListCode) -
            dowJonesSortOrder.indexOf(b.code as DowJonesCustomListCode)
        )
        .map((customList) =>
          columnHelper.accessor(
            ({ fieldLists }) => fieldLists.find((fieldList) => fieldList.listId === customList.id),
            {
              id: customList.name,
              header: () => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {dowJonesListDisplayNamesOLD[customList.code as DowJonesCustomListCode]}
                </div>
              ),
              cell: ({ row, getValue }) => {
                const fieldList = getValue();
                const confCopy = [...significantFieldConf];
                const fields = confCopy.find((conf) => conf.listId === fieldList?.listId)?.fields;
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {fieldList ? (
                      <SalvCheckbox
                        ariaLabel={`${row.original.fieldName} ${customList.name}`}
                        selected={fields?.includes(fieldList.fieldId)}
                        onChange={(isSelected) => {
                          if (isSelected) {
                            if (fields) {
                              fields.push(fieldList.fieldId);
                            } else {
                              confCopy.push({
                                listId: fieldList.listId!,
                                fields: [fieldList.fieldId],
                              });
                            }
                          } else {
                            fields?.splice(fields!.indexOf(fieldList.fieldId), 1);
                          }
                          setSignificantFieldConf(confCopy);
                        }}
                      />
                    ) : (
                      <SalvTooltipWrapper tooltip={'This field does not exist for the highlighted list'}>
                        <FontAwesomeIcon icon={faMinus} style={{ color: 'var(--secondary-default)' }} />
                      </SalvTooltipWrapper>
                    )}
                  </div>
                );
              },
            }
          )
        ) || []),
    ],
    [customLists, significantFieldConf]
  );

  return (
    <>
      <SalvButton
        label={'Edit'}
        variant={'muted'}
        size={buttonSize}
        onClick={() => setShowModal(true)}
        icon={buttonSize === Size.EXTRA_SMALL ? faPencil : undefined}
      />
      <SalvModal show={showModal} onHide={handleClose} dialogClassName={'dow-jones-significant-fields-modal'}>
        <SalvModal.Header>Significant Update Indicators from List records </SalvModal.Header>
        <SalvModal.Body>
          <div className={'text-s m-b-16'}>
            {entityType === InternalApiEntityType.PERSON
              ? 'Select which changes in the List’s records are significant enough to entail a a full re-screening of the Person during the subsequent screening event.'
              : 'Select which changes in the List’s records are significant enough to entail a match to be removed from the goodlist'}
          </div>
          <SalvTable columns={columns} data={significantFieldsByField} />
        </SalvModal.Body>
        <SalvModal.Footer>
          <SalvButton label='Close' variant='muted' size={Size.LARGE} onClick={() => handleClose()} />
          <SalvButton label='Save' variant='primary' size={Size.LARGE} disabled={submitting} onClick={() => submit()} />
        </SalvModal.Footer>
      </SalvModal>
    </>
  );
};

export default DowJonesSignificantFieldsModal;
